import React, { useContext } from 'react';

import { SourceAbbr } from '@dndbeyond/character-common-components/es';
import { CreatureBlock, MarketplaceCta } from '@dndbeyond/character-components/es';
import {
    AccessUtils,
    CharacterTheme,
    Creature,
    CreatureUtils,
    Extra,
    ExtraUtils,
    RuleData,
    RuleDataUtils,
    SnippetData,
    Vehicle,
    VehicleUtils,
} from '@dndbeyond/character-rules-engine/es';

import VehicleBlock from '../../../../components/VehicleBlock';
import { RemoveButton } from '../../../../components/common/Button';
import { ExtrasManagerContext } from '../../../../managers/ExtrasManagerContext';
import { ComponentUtils, TypeScriptUtils } from '../../../../utils';
import ExtraManagePaneListingExtra from '../ExtraManagePaneListingExtra';

interface Props {
    extra: Extra;
    ruleData: RuleData;
    onRemove: (extra: Extra) => void;
    isReadonly: boolean;
    theme: CharacterTheme;
    snippetData: SnippetData;
}

export const ExtraManagePaneCurrentListing: React.FC<Props> = ({
    extra,
    ruleData,
    onRemove,
    isReadonly,
    theme,
    snippetData,
}) => {
    const { extrasManager } = useContext(ExtrasManagerContext);

    const extraData = extrasManager.getExtraData(extra);

    let label: string = 'Extra';
    let ContentComponent: React.ReactNode;

    if (extrasManager.isCreature(extra)) {
        label = 'Creature';
        ContentComponent = (
            <CreatureBlock creature={extraData as Creature} ruleData={ruleData} snippetData={snippetData} />
        );
    } else if (extrasManager.isVehicle(extra)) {
        label = 'Vehicle';
        const vehicle = extraData as Vehicle;
        ContentComponent = AccessUtils.isAccessible(VehicleUtils.getAccessType(vehicle)) ? (
            <VehicleBlock theme={theme} {...ComponentUtils.generateVehicleBlockProps(vehicle, ruleData)} />
        ) : (
            <MarketplaceCta
                sourceNames={extrasManager.getSourceNames(extra)}
                description={`To unlock this ${label}, check out the Marketplace to view purchase options.`}
            />
        );
    }

    let sourceAbbrs: Array<React.ReactNode> = [];
    if (extrasManager.isCreature(extra)) {
        const sources = ExtraUtils.getSources(extra);
        sourceAbbrs = extrasManager.isHomebrew(extra)
            ? [<SourceAbbr isDarkMode={theme.isDarkMode} sourceName="Homebrew" />]
            : sources
                  .map((sourceMapping) => RuleDataUtils.getSourceDataInfo(sourceMapping.sourceId, ruleData))
                  .filter(TypeScriptUtils.isNotNullOrUndefined)
                  .map((source) => {
                      return <SourceAbbr sourceName={source.description} isDarkMode={theme.isDarkMode} />;
                  });
    }

    return (
        <ExtraManagePaneListingExtra
            headerCallout={<RemoveButton onClick={() => onRemove(extra)} />}
            metaItems={[...ExtraUtils.getMetaText(extra), ...sourceAbbrs]}
            heading={ExtraUtils.getName(extra)}
        >
            {ContentComponent}
        </ExtraManagePaneListingExtra>
    );
};

export default ExtraManagePaneCurrentListing;
